import { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import type { DiffAction, ResolutionChoice } from "./profile-merge-types";
import { choiceOptions } from "./profile-merge-labels";
import {
  useProfileMergeStore,
  guideCounter,
  guideSteps,
  stepDecided,
  stepEffectiveChoice,
} from "@/stores/profile-merge-store";

// The guided review's per-card chrome, shared by the three merge cards
// (person_detail field, collection row, skills group). Exactly one card is the
// guide's ACTIVE step: it carries the decision buttons (Keep mine/Use imported
// or Skip/Add — deciding advances the guide to the next unreviewed card), the
// step counter, and back/next. Every OTHER card shows a Review button plus:
// — if the user has already decided it, the chosen resolution ("Keeping your
//   value" / "Will be added" / …);
// — if not yet decided, a neutral "Not reviewed yet" (NOT its default
//   resolution — that read as already-decided and contradicted the bar's
//   "N fields left to review", see the review-status alignment).
// Clicking or focusing anywhere in a card pulls the guide to it.

const STATUS = {
  KEEP_EXISTING: {
    ADD: { id: "profile.merge.status.skip", defaultMessage: "Skipped" },
    CONFLICT: { id: "profile.merge.status.keep", defaultMessage: "Keeping your value" },
  },
  USE_INCOMING: {
    ADD: { id: "profile.merge.status.add", defaultMessage: "Will be added" },
    CONFLICT: { id: "profile.merge.status.use", defaultMessage: "Using the imported value" },
  },
} as const;

// JS-initiated smooth scrolling ignores the CSS reduced-motion pairing the
// motion canon relies on, so honor the preference here explicitly.
function scrollBehavior(): ScrollBehavior {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

export function MergeGuideControls({
  stepKey,
  action,
  ariaLabel,
  useIncomingDisabled,
}: {
  stepKey: string;
  action: DiffAction;
  // Overrides the decision group's label (the skills card describes the
  // all-at-once semantics); defaults to the per-item keep/use label.
  ariaLabel?: string;
  // Disables the Add/Use button while taking the row would commit an invalid
  // value (an unresolved language awaiting its chooser pick).
  useIncomingDisabled?: boolean;
}) {
  const intl = useIntl();
  const preview = useProfileMergeStore((s) => s.preview);
  const choices = useProfileMergeStore((s) => s.choices);
  const guideKey = useProfileMergeStore((s) => s.guideKey);
  const guideNonce = useProfileMergeStore((s) => s.guideNonce);
  const decide = useProfileMergeStore((s) => s.decide);
  const activateStep = useProfileMergeStore((s) => s.activateStep);

  const active = guideKey === stepKey;
  const markGuideNavHandled = useProfileMergeStore((s) => s.markGuideNavHandled);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  // The guide scrolls to THIS row, not to the card. The decision buttons sit at
  // the card's bottom edge and a CONFLICT card (both sides of every editable
  // field, stacked) is routinely taller than the viewport — centering the card
  // then puts its middle on screen and leaves the buttons below the fold, so the
  // walk lands focus on a control the user cannot see. Anchoring the controls
  // keeps the decision at a consistent spot every step, with as much of the card
  // as fits above it. Its scroll-margins keep it clear of the topbar and of the
  // floating merge bar, which paints over the bottom strip of the viewport.
  const controlsRef = useRef<HTMLDivElement>(null);
  // Distinguishes the three ways this card can find itself active. A
  // programmatic guide move (decide-advance / back / next) is pending while
  // guideNonce is ahead of the store's handled marker — the marker lives in
  // the STORE because a move can unmount its destination's whole subtree (the
  // Details-tab switch), so the freshly mounted card must still see it.
  // wasActive/firstSight make the other branches one-shot per transition, so a
  // StrictMode double-run can't re-fire them.
  const prevActive = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    const wasActive = prevActive.current;
    const firstSight = !mounted.current;
    prevActive.current = active;
    mounted.current = true;
    if (!active) return;
    if (useProfileMergeStore.getState().guideNonceHandled !== guideNonce) {
      // A guide move landed here — whether this card was already mounted or
      // was just mounted by the tab switch the move triggered. Take focus so
      // the keyboard walk continues without a dead end.
      markGuideNavHandled();
      // preventScroll, then scroll deliberately: the browser's own focus scroll
      // is a minimal reveal that ignores the fixed merge bar, so it can park the
      // button under it. Centering the controls puts the decision mid-viewport.
      firstButtonRef.current?.focus({ preventScroll: true });
      controlsRef.current?.scrollIntoView?.({ behavior: scrollBehavior(), block: "center" });
    } else if (firstSight) {
      // First render as the active step with no move pending: initial preview
      // start, or a manual tab flip back to the active card. Reveal the decision
      // without stealing focus, and without moving a card already fully in view.
      controlsRef.current?.scrollIntoView?.({ behavior: scrollBehavior(), block: "nearest" });
    } else if (!wasActive && document.activeElement === document.body) {
      // Click/focus activation swapped the inactive chrome out from under the
      // focused element (the Review button unmounts), dropping focus to the
      // body — a dead end for keyboard users. Hand it to the first decision
      // button. Focus resting anywhere real (an InlineEdit input, the tab
      // toggle) is left alone.
      firstButtonRef.current?.focus({ preventScroll: true });
    }
  }, [active, guideNonce, markGuideNavHandled]);

  const step = guideSteps(preview).find((s) => s.key === stepKey) ?? null;
  // Position/total over the steps still LEFT TO REVIEW, so the counter counts
  // down with the bar rather than climbing against it — see guideCounter.
  const counter = active ? guideCounter(preview, choices, stepKey) : null;
  const decided = step ? stepDecided(step, choices) : false;
  const choice: ResolutionChoice | null = step
    ? stepEffectiveChoice(preview, step, choices)
    : null;

  // A DECIDED card that isn't the active one collapses to its chosen resolution
  // + a Review button (click to reopen the buttons and change it). Every card
  // still left to review shows its decision buttons directly — no Review detour —
  // so an unreviewed field always reads as actionable and the bar's "N left to
  // review" == the number of button-bearing cards.
  if (!active && decided) {
    const status = choice ? STATUS[choice][action === "ADD" ? "ADD" : "CONFLICT"] : null;
    return (
      <div className="flex flex-wrap items-center gap-2" id={`profile-merge-status-${stepKey}`}>
        {status ? <span className="smycles-field-meta text-ink-muted">{intl.formatMessage(status)}</span> : null}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => activateStep(stepKey)}
          id={`profile-merge-review-${stepKey}`}
        >
          <FormattedMessage id="profile.merge.guide.review" defaultMessage="Review" />
        </Button>
      </div>
    );
  }

  const options = choiceOptions(intl, action);

  return (
    <div
      ref={controlsRef}
      // scroll-mb clears the floating merge bar (bottom-8 + its own height), so a
      // `nearest` reveal never parks the decision behind it; scroll-mt clears the topbar.
      className="flex scroll-mt-24 scroll-mb-28 flex-wrap items-center gap-2"
      id={`profile-merge-guide-${stepKey}`}
    >
      <div
        role="group"
        aria-label={
          ariaLabel ??
          intl.formatMessage({
            id: "profile.merge.chooseAria",
            defaultMessage: "Keep your value or use the imported one",
          })
        }
        // Wraps rather than overflows: an unwrappable pair grew past a narrow
        // card into its neighbour, which paints on top and swallowed the click
        // (spark#174). Cards now span the full grid row, so this is the backstop
        // for any card still too narrow for the pair (narrow viewport, long
        // translation) — it keeps the decision inside the card, always clickable.
        className="flex min-w-0 flex-wrap items-center gap-1.5"
      >
        {options.map((option, i) => (
          <Button
            key={option.value}
            id={`profile-merge-choose-${stepKey}-${option.value}`}
            // The active card owns the focus ref so the keyboard walk (decide →
            // advance → focus next) lands on the right card.
            ref={active && i === 0 ? firstButtonRef : undefined}
            variant={choice === option.value ? "primary" : "secondary"}
            size="compact"
            aria-pressed={choice === option.value}
            disabled={option.value === "USE_INCOMING" && useIncomingDisabled}
            onClick={() => decide(stepKey, option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      {/* The step counter marks the ACTIVE card only — the one the bar's
          Previous/Next currently points at — so the several button-bearing
          cards don't each sprout a counter. Nav lives in the bar. */}
      {counter ? (
        <span className="ml-auto smycles-field-meta text-ink-muted" id={`profile-merge-step-${stepKey}`}>
          <FormattedMessage
            id="profile.merge.guide.step"
            defaultMessage="{current, number} of {total, number}"
            values={{ current: counter.current, total: counter.total }}
          />
        </span>
      ) : null}
    </div>
  );
}
